import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { IconEye, IconHistory, IconInfoCircle } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { WorkflowStatusBadge } from "./field"
import { PledgeReportHistory } from "./pledge-report-history"
import { activityDate, benefitActivity, type BenefitActivity } from "@/lib/benefit-activity"
import { ownerDisplay } from "@/lib/benefit-ownership"
import { fmtDate } from "@/lib/benefit-format"
import type { BenefitDetailDto, BenefitWorkflowResponse } from "@/lib/benefit-types"
import type { BenefitHistoryScope } from "@/lib/benefit-section-tabs"
import "./application-history.css"

export function ApplicationHistory({ benefit, workflow, scope, onScopeChange, children }: {
  benefit: BenefitDetailDto
  workflow: BenefitWorkflowResponse | null
  scope: BenefitHistoryScope
  onScopeChange: (scope: BenefitHistoryScope) => void
  children: ReactNode
}) {
  const [selected, setSelected] = useState<BenefitActivity | null>(null)
  const returnFocus = useRef<string | null>(null)
  const eventButtons = useRef(new Map<string, HTMLButtonElement>())
  const administrationHeading = useRef<HTMLHeadingElement | null>(null)
  const heading = useRef<HTMLHeadingElement | null>(null)
  const records = useMemo(() => benefitActivity(benefit, workflow), [benefit, workflow])
  const visible = records.filter(record => scope === "all" || record.scope === scope)
  const workflowUnavailable = !workflow || !["V2", "Legacy"].includes(workflow.authority) || workflow.display?.readUnavailable === true

  // Details are a transient read-only disclosure, not a retained draft. Any changed
  // readback (including unversioned history) or URL filter closes the old display row.
  useEffect(() => {
    // A refresh can first remove workflow authority, then replace a saved status row.
    // Focus the stable heading rather than an opener that the next read may remove.
    returnFocus.current = null
    setSelected(null)
  }, [records, scope])

  return <section className="history-workspace" aria-labelledby="application-history-heading">
    <header>
      <p className="history-eyebrow">Audit trail & file administration</p>
      <h2 id="application-history-heading" ref={heading} tabIndex={-1}>Application & grant history</h2>
      <p className="history-description">Review recorded application, grant and correction activity. Routine grant actions stay with the awards.</p>
    </header>
    <div className="history-summary">
      <dl>
        <div><dt>File</dt><dd>{benefit.fileNumber || "Not recorded"}</dd></div>
        <div><dt>Intake owner</dt><dd>{ownerDisplay(benefit.ownership)}</dd></div>
        <div><dt>Application date</dt><dd>{fmtDate(benefit.applicationDate)}</dd></div>
        <div><dt>Workflow</dt><dd><WorkflowStatusBadge workflow={workflow} /></dd></div>
      </dl>
      <Button variant="outline" size="sm" onClick={() => {
        administrationHeading.current?.focus({ preventScroll: true })
        administrationHeading.current?.scrollIntoView({ block: "start" })
      }}>View administration</Button>
    </div>
    <Card>
      <CardHeader>
        <CardTitle><h3>Activity</h3></CardTitle>
        <CardDescription>Original source records and explanations are preserved. Earlier status records are not current workflow authority.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="history-filter">
          <ToggleGroup type="single" value={scope} onValueChange={value => { if (value === "all" || value === "application" || value === "grants") onScopeChange(value) }} variant="outline" size="sm" aria-label="Activity scope">
            <ToggleGroupItem value="all">All activity</ToggleGroupItem>
            <ToggleGroupItem value="application">Application</ToggleGroupItem>
            <ToggleGroupItem value="grants">Grants</ToggleGroupItem>
          </ToggleGroup>
          <span className="text-xs text-muted-foreground" role="status">{visible.length} of {records.length} records</span>
        </div>
        {workflowUnavailable && <p role="status" className="history-notice"><IconInfoCircle aria-hidden="true" className="size-4 shrink-0" />Workflow activity is unavailable or loading. Only the saved status records below can be shown; this is not a complete activity history.</p>}
        <TooltipProvider>
          <Table aria-label="Application activity" className="history-table" tabIndex={0}>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Scope / source</TableHead><TableHead>Event / explanation</TableHead><TableHead>Actor</TableHead><TableHead>Details</TableHead></TableRow></TableHeader>
            <TableBody>
              {visible.map(record => <TableRow key={record.key}>
                <TableCell className="history-when">{activityDate(record.occurredAt)}</TableCell>
                <TableCell><Badge variant="outline">{record.scopeLabel}</Badge><span className="history-source">{record.source}</span></TableCell>
                <TableCell className="history-event"><strong>{record.event}</strong>{record.reason && <span className="history-source">{record.reason}</span>}{record.explanation && <p className="history-explanation">{record.explanation}</p>}</TableCell>
                <TableCell className="history-actor">{record.actor}</TableCell>
                <TableCell><Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" aria-label={`View activity: ${record.scopeLabel} · ${record.event} · ${activityDate(record.occurredAt)}`} ref={element => { if (element) eventButtons.current.set(record.key, element); else eventButtons.current.delete(record.key) }} onClick={() => {
                  returnFocus.current = record.key
                  setSelected(record)
                }}><IconEye className="size-4" /></Button></TooltipTrigger><TooltipContent>View recorded event</TooltipContent></Tooltip></TableCell>
              </TableRow>)}
              {!visible.length && <TableRow><TableCell colSpan={5} className="history-empty">{workflowUnavailable ? "No saved status records were returned for this scope." : "No activity records were returned for this scope."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TooltipProvider>
      </CardContent>
    </Card>
    <PledgeReportHistory benefitId={benefit.id} />
    <p className="history-footnote"><IconHistory className="size-4 shrink-0" aria-hidden="true" />This view combines returned records without rewriting or merging them. Grant review, vendor pledges, export and payment-list inclusion remain separate facts.</p>
    <section className="history-administration" aria-labelledby="file-administration-heading">
      <div><h2 id="file-administration-heading" ref={administrationHeading} tabIndex={-1}>File administration</h2><p className="history-description">Application-wide facts and authorized actions. Opening this section does not change the file.</p></div>
      {children}
    </section>
    <Sheet open={selected !== null} onOpenChange={open => { if (!open) setSelected(null) }}>
      <SheetContent className="history-sheet w-full sm:max-w-xl" onCloseAutoFocus={event => {
        event.preventDefault()
        const button = returnFocus.current ? eventButtons.current.get(returnFocus.current) : null
        if (button?.isConnected) button.focus()
        else heading.current?.focus()
      }}>
        <SheetHeader><p className="history-eyebrow">{selected?.source ?? "Recorded activity"}</p><SheetTitle>Activity details</SheetTitle><SheetDescription><span className="block font-medium mb-1">{selected?.scopeLabel}</span>Read-only source information. This event does not replace the current workflow or payment status.</SheetDescription></SheetHeader>
        <div className="history-detail-body" role="region" aria-label="Activity details content" tabIndex={0}>
          {selected && <>
            <div className="history-recorded-event"><h3>Recorded event</h3><p>{selected.event}</p>{selected.reason && <p>{selected.reason}</p>}<h3>Explanation</h3><p className="history-explanation">{selected.explanation || "No explanation was recorded."}</p></div>
            <dl className="history-detail-facts">
              <div><dt>When</dt><dd>{activityDate(selected.occurredAt)}</dd></div><div><dt>Scope</dt><dd>{selected.scopeLabel}</dd></div>
              <div><dt>Actor</dt><dd>{selected.actor}</dd></div><div><dt>Source</dt><dd>{selected.source}</dd></div>
              <div><dt>Recorded timestamp</dt><dd>{selected.occurredAt || "Not recorded"}</dd></div>
              {selected.actorId && <div><dt>Actor identifier</dt><dd>{selected.actorId}</dd></div>}
              {selected.statusCode != null && <div><dt>Recorded status code</dt><dd>{selected.statusCode}</dd></div>}
              {selected.reasonCode != null && <div><dt>Recorded reason</dt><dd>{selected.reasonGroup ?? "Unspecified group"} / {selected.reasonCode}</dd></div>}
              {selected.eventId && <div><dt>Event identifier</dt><dd>{selected.eventId}</dd></div>}
              {selected.snapshotId && <div><dt>Snapshot identifier</dt><dd>{selected.snapshotId}</dd></div>}
            </dl>
          </>}
        </div>
        <SheetFooter><p className="text-xs text-muted-foreground">Viewing this event does not save or change anything.</p><Button variant="outline" onClick={() => setSelected(null)}>Done</Button></SheetFooter>
      </SheetContent>
    </Sheet>
  </section>
}
